import { createpaymentRequestReminderAsync } from "@api";
import { useUser } from "@auth";
import { toast } from "@components/toast/Toast";
import {
  Button,
  CurrencyInput,
  EmailInput,
  FormError,
  FormGroup,
  FormLabel,
  MobilePhoneInput,
  Modal,
  Tab,
  TabGroup,
  TextInput
} from "@components";
import { formatAmount } from "@utils";
import { useState } from "react";
import { ChatLeftDots, CheckLg, Envelope } from "react-bootstrap-icons";
import { useForm } from 'react-hook-form';
import { DEFAULT_REMINDER_SUBJECT, buildDefaultReminderMessage } from "./reminderDefaults";
import { buildPaymentUrl } from "./reminder";

const SendReminder = ({ onComplete, paymentRequest, isOpen, onClose }) => {
  if (!paymentRequest) return null;

  const { user } = useUser();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [communicationMethods, setCommunicationMethods] = useState({
    sms: paymentRequest.communicationMethod !== "Email",
    email: paymentRequest.communicationMethod === "Email",
  });

  const { control, handleSubmit, watch, reset, formState: { errors }, clearErrors } = useForm({
    defaultValues: {
      amount: paymentRequest.amountRequested,
      reference: paymentRequest.reference,
      phone: paymentRequest.sms?.to || "",
      email: paymentRequest.email?.to || "",
      subject: DEFAULT_REMINDER_SUBJECT,
      message: buildDefaultReminderMessage(paymentRequest)
    }
  });

  const radios = [
    { name: "Send SMS", value: "sms", icon: ChatLeftDots },
    { name: "Send Email", value: "email", icon: Envelope },
  ];

  const getCommMethod = () => {
    if (communicationMethods.sms) return "Sms";
    if (communicationMethods.email) return "Email";
    // if (communicationMethods.sms && !communicationMethods.email) return "Sms";
    // if (communicationMethods.email && !communicationMethods.sms) return "Email";
    // if (communicationMethods.sms && communicationMethods.email) return "SmsEmail";
  }

  const handleCommsSelected = (option) => {
    let newOptions = null;

    newOptions = {
      sms: option === "sms",
      email: option === "email",
    };


    if (newOptions !== communicationMethods) {
      clearErrors();
    }

    setCommunicationMethods(newOptions)
  };


  async function onSubmit(formData) {
    setLoading(true);

    try {
      await createpaymentRequestReminderAsync(
        paymentRequest.id,
        buildPaymentUrl(paymentRequest.id),
        getCommMethod(),
        communicationMethods.sms ? formData.phone : null,
        communicationMethods.email ? formData.email : null,
        communicationMethods.email ? formData.subject : null,
        communicationMethods.sms ? formData.message : null,
        communicationMethods.email ? formData.message : null);
    } catch (error) {
      setLoading(false);
      toast({ description: "Couldn't send the reminder. Please try again." });
      return;
    }

    setSubmitted(formData);
    setLoading(false);
    toast({ description: `Sent reminder for ${formatAmount(formData.amount)}` });

    if (onComplete) {
      await onComplete();
    }
  }

  const close = () => {
    onClose();
    reset({
      amount: paymentRequest.amountRequested,
      reference: paymentRequest.reference,
      phone: paymentRequest.sms?.to || "",
      email: paymentRequest.email?.to || "",
      subject: DEFAULT_REMINDER_SUBJECT,
      message: buildDefaultReminderMessage(paymentRequest)
    });
    setTimeout(() => setSubmitted(null), 200);
    ;
  }

  return (<Modal isOpen={isOpen} onClose={close} title="Send reminder">
    {submitted
      ? <>
        <div className="bg-emerald-50 rounded-full size-16 flex items-center justify-center mx-auto my-5">
          <CheckLg className="text-emerald-900" size={24} />
        </div>
        <h5 className="mb-5 font-semibold text-center">{communicationMethods.sms ? "SMS" : "Email"} reminder sent</h5>
        <p className="mb-5 text-sm text-zinc-500 text-center">Reminder sent to {paymentRequest.customerName}</p>
      </>
      : (<>
        <TabGroup className="mb-5 px-5">
          {radios.map((radio, idx) => (
            <Tab
              key={idx}
              id={`radio-${idx}`}
              title={radio.name}
              value={radio.value}
              active={communicationMethods[radio.value]}
              onClick={() => handleCommsSelected(radio.value)}>
              <radio.icon className="mr-2" />{radio.name}
            </Tab>
          ))}
        </TabGroup>
        <form onSubmit={handleSubmit(onSubmit)} className="p-5 pt-0">
          <FormGroup>
            <FormLabel>Amount</FormLabel>
            <CurrencyInput
              name="amount"
              control={control}
              displayName="Amount"
              disabled={true} />
            <FormError name="amount" errors={errors} />
          </FormGroup>
          <FormGroup>
            <FormLabel>Reference</FormLabel>
            <TextInput
              displayName="Reference"
              control={control}
              disabled={true}
              name="reference"
              placeholder="INV100001"
              errors={errors.reference}
            />
            <FormError name="reference" errors={errors} />
          </FormGroup>
          {communicationMethods.sms && (
            <FormGroup>
              <FormLabel>Customer mobile</FormLabel>
              <MobilePhoneInput
                name="phone"
                control={control}
                displayName="Customer mobile"
                errors={errors.phone}
                required={true}

              />
              <FormError name="phone" errors={errors} />
            </FormGroup>
          )}
          {communicationMethods.email && (
            <FormGroup>
              <FormLabel>Customer email</FormLabel>
              <EmailInput
                name="email"
                control={control}
                displayName="Customer email"
                errors={errors.email}
                required={true}
                placeholder="your@customer.co.uk"

              />
              <FormError name="email" errors={errors} />
            </FormGroup>
          )}
          {communicationMethods.email && (
            <FormGroup>
              <FormLabel>Subject</FormLabel>
              <TextInput
                name="subject"
                info="The text you enter here will appear in the email subject line."
                control={control}
                displayName="Subject"
                errors={errors.subject}
                required={true}
              />
              <FormError name="subject" errors={errors} />
            </FormGroup>
          )}
          {(communicationMethods.sms || communicationMethods.email) &&
            <FormGroup>
              <FormLabel>Message</FormLabel>
              <TextInput
                name="message"
                control={control}
                displayName="Message"
                errors={errors.message}
                required={true}
                area={true}
              />
              <FormError name="message" errors={errors} />
            </FormGroup>
          }
        </form></>)}
    <div className="p-5 flex">
      {!submitted ? <Button
        color="primary"
        type="submit"
        className="ml-auto"
        disabled={loading}
        onClick={handleSubmit(onSubmit)}
      >
        Send reminder
      </Button>
        : <Button
          color="primary"
          className="w-full"
          type="submit"
          onClick={() => close()}
        >
          Return to dashboard
        </Button>}
    </div>
  
  </Modal>);
}

export default SendReminder;
